import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  Banknote,
  Receipt,
  Repeat,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import { formatDate, formatMoney } from "../lib/format";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { BillingDashboard } from "../api/types";

/** First day of the current month, in the browser's zone. */
function monthStart(): string {
  const now = new Date();
  const local = new Date(now.getFullYear(), now.getMonth(), 1);
  return iso(local);
}

function todayISO(): string {
  return iso(new Date());
}

function iso(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function shift(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return iso(d);
}

/**
 * The Billing landing page.
 *
 * Every figure comes from the ledger -- invoices and payments -- so it can never
 * disagree with the register. The headline totals follow the selected period;
 * outstanding and overdue are always as of today.
 */
export function BillingDashboardPage() {
  const timeZone = useAccountTimeZone();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayISO());

  const dashboard = useQuery({
    queryKey: ["billing", "dashboard", start, end],
    queryFn: ({ signal }) => api.billingDashboard({ start, end }, signal),
  });
  const ageing = useQuery({
    queryKey: ["billing", "ageing"],
    queryFn: ({ signal }) => api.billingAgeing(signal),
  });
  const balances = useQuery({
    queryKey: ["billing", "customer-balances"],
    queryFn: ({ signal }) => api.billingCustomerBalances(signal),
  });

  const presets: Array<[string, () => void]> = [
    ["This month", () => { setStart(monthStart()); setEnd(todayISO()); }],
    ["Last 30 days", () => { setStart(shift(30)); setEnd(todayISO()); }],
    ["Last 90 days", () => { setStart(shift(90)); setEnd(todayISO()); }],
    ["Year to date", () => { setStart(`${new Date().getFullYear()}-01-01`); setEnd(todayISO()); }],
  ];

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle="Collections, outstanding balances and what is overdue."
        actions={
          <>
            <Link
              to="/billing/reports"
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
            >
              Reports
            </Link>
            <Link
              to="/billing/invoices"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Receipt size={14} /> Invoices
            </Link>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            From
          </span>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            aria-label="Period start"
            className="rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            To
          </span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            aria-label="Period end"
            className="rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </label>
        {presets.map(([label, apply]) => (
          <button
            key={label}
            onClick={apply}
            className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
          >
            {label}
          </button>
        ))}
      </div>

      {dashboard.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i}>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-3 h-7 w-32" />
            </Card>
          ))}
        </div>
      ) : dashboard.isError ? (
        <ErrorCard error={dashboard.error} />
      ) : dashboard.data ? (
        <DashboardBody data={dashboard.data} timeZone={timeZone} start={start} end={end} />
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Receivables ageing</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Open balances by how long they have been owed.
            </p>
          </div>
          {ageing.isPending ? (
            <div className="p-5">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : ageing.data ? (
            <ul className="divide-y divide-slate-100">
              {ageing.data.buckets.map((bucket) => (
                <li key={bucket.key} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm text-slate-700">{bucket.label}</p>
                    <p className="text-xs text-slate-500">
                      {bucket.count} {bucket.count === 1 ? "invoice" : "invoices"}
                    </p>
                  </div>
                  <span
                    className={
                      bucket.key === "days_90_plus" && Number(bucket.amount?.amount ?? 0) > 0
                        ? "text-sm font-medium tabular-nums text-rose-700"
                        : "text-sm font-medium tabular-nums text-slate-900"
                    }
                  >
                    {formatMoney(bucket.amount)}
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between bg-slate-50 px-5 py-3">
                <span className="text-sm font-semibold text-slate-900">Total</span>
                <span className="text-sm font-semibold tabular-nums text-slate-900">
                  {formatMoney(ageing.data.total)}
                </span>
              </li>
            </ul>
          ) : null}
        </Card>

        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Customer balances</h2>
            <p className="mt-0.5 text-xs text-slate-500">Who owes the most right now.</p>
          </div>
          {balances.isPending ? (
            <div className="p-5">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (balances.data?.results.length ?? 0) === 0 ? (
            <EmptyState title="Nothing outstanding" body="Every invoice is settled." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {balances.data?.results.map((row) => (
                <li key={row.contact} className="flex items-center justify-between px-5 py-3">
                  <Link
                    to={`/customers/contacts/${row.contact}`}
                    className="text-sm text-slate-700 hover:underline"
                  >
                    {row.contact_name || "Unknown"}
                  </Link>
                  <span className="text-sm font-medium tabular-nums text-slate-900">
                    {formatMoney(row.balance)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function DashboardBody({
  data,
  timeZone,
  start,
  end,
}: {
  data: BillingDashboard;
  timeZone: string | undefined;
  start: string;
  end: string;
}) {
  const maxBar = Math.max(
    1,
    ...data.trend.map((point) => Number(point.invoiced?.amount ?? 0)),
    ...data.trend.map((point) => Number(point.collected?.amount ?? 0)),
  );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Invoiced"
          value={formatMoney(data.invoiced)}
          sub={`${start} → ${end}`}
          icon={<ArrowUpRight size={16} />}
          tone="slate"
          to="/billing/invoices"
        />
        <Tile
          label="Collected"
          value={formatMoney(data.collected)}
          sub="net of refunds"
          icon={<Banknote size={16} />}
          tone="emerald"
          to="/billing/payments"
        />
        <Tile
          label="Outstanding"
          value={formatMoney(data.outstanding)}
          sub="as of today"
          icon={<Receipt size={16} />}
          tone="sky"
          to="/billing/reports"
        />
        <Tile
          label="Overdue"
          value={formatMoney(data.overdue_amount)}
          sub={`${data.overdue_count} invoice${data.overdue_count === 1 ? "" : "s"}`}
          icon={<AlertTriangle size={16} />}
          tone="rose"
          to="/billing/reports"
        />
      </div>

      <BillingDemoTour data={data} />

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Sales and collections</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Last {data.trend.length} months · blue invoiced, green collected.
            </p>
          </div>
          <div className="space-y-4 px-5 py-4">
            {data.trend.map((point) => (
              <div key={point.period}>
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                  <span>{point.period}</span>
                  <span className="tabular-nums">{formatMoney(point.invoiced)} invoiced</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-sky-500"
                    style={{ width: `${(Number(point.invoiced?.amount ?? 0) / maxBar) * 100}%` }}
                  />
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${(Number(point.collected?.amount ?? 0) / maxBar) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">By status</h2>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.status_breakdown.map((row) => (
              <li key={row.status} className="flex items-center justify-between px-5 py-3">
                <InvoiceStatusBadge status={row.status} />
                <div className="text-right">
                  <p className="text-sm tabular-nums text-slate-900">{formatMoney(row.total)}</p>
                  <p className="text-xs text-slate-500">{row.count}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Recent invoices</h2>
            <Link to="/billing/invoices" className="text-xs font-medium text-slate-600 hover:underline">
              All invoices
            </Link>
          </div>
          {data.recent_invoices.length === 0 ? (
            <EmptyState title="No invoices yet" body="Draft one to get started." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/billing/invoices/${invoice.id}`}
                      className="text-sm font-medium text-slate-800 hover:underline"
                    >
                      {invoice.number || "Draft"}
                    </Link>
                    <p className="truncate text-xs text-slate-500">
                      {invoice.contact_name || "Unknown"} ·{" "}
                      {formatDate(invoice.issue_date, timeZone)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums text-slate-900">
                      {formatMoney(invoice.total_amount)}
                    </p>
                    <InvoiceStatusBadge status={invoice.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Recent credit notes</h2>
            <Link
              to="/billing/credit-notes"
              className="text-xs font-medium text-slate-600 hover:underline"
            >
              All credit notes
            </Link>
          </div>
          {data.recent_credit_notes.length === 0 ? (
            <EmptyState
              title="No credit notes"
              body="Corrections to issued invoices appear here."
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recent_credit_notes.map((note) => (
                <li key={note.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/billing/credit-notes/${note.id}`}
                      className="text-sm font-medium text-slate-800 hover:underline"
                    >
                      {note.number || "Credit note"}
                    </Link>
                    <p className="truncate text-xs text-slate-500">
                      {note.contact_name || "Unknown"} · {formatDate(note.issue_date, timeZone)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums text-rose-700">
                      −{formatMoney(note.total_amount)}
                    </p>
                    <Badge tone={note.status === "void" ? "slate" : "amber"}>
                      <span className="capitalize">{note.status}</span>
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card padded={false}>
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Overdue receivables</h2>
            <p className="mt-0.5 text-xs text-slate-500">The oldest unpaid invoices first.</p>
          </div>
          {data.overdue_invoices.length === 0 ? (
            <EmptyState title="Nothing overdue" body="Every issued invoice is on time." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.overdue_invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/billing/invoices/${invoice.id}`}
                      className="text-sm font-medium text-slate-800 hover:underline"
                    >
                      {invoice.number || "Draft"}
                    </Link>
                    <p className="truncate text-xs text-slate-500">
                      {invoice.contact_name || "Unknown"} · due{" "}
                      {formatDate(invoice.due_on, timeZone)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums text-rose-700">
                      {formatMoney(invoice.balance_due)}
                    </p>
                    <p className="text-xs text-slate-500">{invoice.days_overdue}d overdue</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold">Recurring schedules</h2>
            <Link
              to="/billing/recurring"
              className="text-xs font-medium text-slate-600 hover:underline"
            >
              All schedules
            </Link>
          </div>
          <div className="grid grid-cols-4 gap-2 px-5 py-4">
            <MiniStat label="Active" value={data.schedule_summary.active} />
            <MiniStat label="Paused" value={data.schedule_summary.paused} />
            <MiniStat label="Completed" value={data.schedule_summary.completed} />
            <MiniStat label="Cancelled" value={data.schedule_summary.cancelled} />
          </div>
          <div className="border-t border-slate-100 px-5 py-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              Recent failures
            </p>
            {data.recurring_failures.length === 0 ? (
              <p className="text-sm text-slate-500">No recent generation failures.</p>
            ) : (
              <ul className="space-y-2">
                {data.recurring_failures.map((failure, index) => (
                  <li key={`${failure.schedule}-${index}`} className="flex items-start gap-2">
                    <Repeat size={14} className="mt-0.5 shrink-0 text-rose-500" />
                    <div className="min-w-0">
                      <Link
                        to={`/billing/recurring/${failure.schedule}`}
                        className="text-sm text-slate-700 hover:underline"
                      >
                        {failure.schedule_name || "Schedule"}
                      </Link>
                      <p className="truncate text-xs text-rose-600">{failure.detail || "Failed"}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <Card className="mt-6" padded={false}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-sm font-semibold">Recent payments</h2>
          <Link to="/billing/payments" className="text-xs font-medium text-slate-600 hover:underline">
            All payments
          </Link>
        </div>
        {data.recent_payments.length === 0 ? (
          <EmptyState title="No payments yet" body="Receipts will appear here as they are recorded." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recent_payments.map((payment) => (
              <li key={payment.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-800">
                    {payment.contact_name || "Unknown"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {payment.receipt_number} · {payment.method} ·{" "}
                    {formatDate(payment.payment_date, timeZone)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={
                      payment.direction === "outbound"
                        ? "text-sm font-medium tabular-nums text-rose-700"
                        : "text-sm font-medium tabular-nums text-emerald-700"
                    }
                  >
                    {payment.direction === "outbound" ? "−" : "+"}
                    {formatMoney(payment.amount)}
                  </span>
                  <Link
                    to={`/billing/payments/${payment.id}/receipt`}
                    className="text-xs font-medium text-slate-600 hover:text-slate-900"
                  >
                    Receipt
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2 text-center">
      <p className="text-lg font-semibold tabular-nums text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  icon,
  tone,
  to,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: ReactNode;
  tone: "emerald" | "sky" | "rose" | "slate";
  /** When set, the whole tile links to the register the number came from. */
  to?: string;
}) {
  const toneClass: Record<string, string> = {
    emerald: "text-emerald-600",
    sky: "text-sky-600",
    rose: "text-rose-600",
    slate: "text-slate-500",
  };
  const card = (
    <Card className={to ? "transition hover:ring-2 hover:ring-slate-300" : undefined}>
      <div className={`flex items-center gap-2 ${toneClass[tone]}`}>
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-slate-500">{sub}</p> : null}
    </Card>
  );
  return to ? (
    <Link to={to} className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
      {card}
    </Link>
  ) : (
    card
  );
}

/**
 * A light, optional walk-through of the billing sample data.
 *
 * It only links to real records already on the dashboard payload -- opening a
 * step never writes anything -- and it is collapsed by default so it never
 * obstructs the normal register work. It renders nothing when the account has no
 * billing history to show.
 */
function BillingDemoTour({ data }: { data: BillingDashboard }) {
  const [open, setOpen] = useState(false);

  const invoice = data.recent_invoices.find((row) => row.status !== "void") ?? data.recent_invoices[0];
  const payment = data.recent_payments.find((row) => row.direction === "inbound") ?? data.recent_payments[0];
  const credit = data.recent_credit_notes[0];
  const anything = Boolean(invoice || payment || credit);
  if (!anything) return null;

  const link = "font-medium text-slate-900 underline hover:text-slate-700";

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">New to Billing? Explore the demo</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            A five-step walk through your own sample records. It only opens links -- no data is changed.
          </p>
        </div>
        <Button variant={open ? "secondary" : "primary"} onClick={() => setOpen((value) => !value)}>
          {open ? "Hide tour" : "Explore Billing demo"}
        </Button>
      </div>

      {open ? (
        <ol className="mt-4 space-y-3 text-sm">
          <DemoStep n={1} title="Open an example invoice">
            {invoice ? (
              <Link className={link} to={`/billing/invoices/${invoice.id}`}>
                {invoice.number || "Draft invoice"}
              </Link>
            ) : (
              "No invoice yet."
            )}
          </DemoStep>
          <DemoStep n={2} title="Inspect its items, tax and total">
            Open the invoice to see each line, the tax applied and the amount due.
          </DemoStep>
          <DemoStep n={3} title="Review a payment and its receipt">
            {payment ? (
              <Link className={link} to={`/billing/payments/${payment.id}/receipt`}>
                {payment.receipt_number || "Payment receipt"}
              </Link>
            ) : (
              "No payment recorded yet."
            )}
          </DemoStep>
          <DemoStep n={4} title="Open a related credit note">
            {credit ? (
              <Link className={link} to={`/billing/credit-notes/${credit.id}`}>
                {credit.number || "Credit note"}
              </Link>
            ) : (
              "No credit note yet."
            )}
          </DemoStep>
          <DemoStep n={5} title="See the effect on reports">
            <Link className={link} to="/billing/reports">
              Billing reports
            </Link>{" "}
            reconcile these records -- invoiced, collected, credited and refunded.
          </DemoStep>
        </ol>
      ) : null}
    </Card>
  );
}

function DemoStep({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
        {n}
      </span>
      <span className="text-slate-700">
        <span className="font-medium text-slate-900">{title}.</span> {children}
      </span>
    </li>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.isPlanLimit) {
    return (
      <Card>
        <EmptyState
          title="Billing is not on your plan"
          body={`${error.message} Upgrade to see collections and receivables.`}
        />
      </Card>
    );
  }
  return (
    <Card>
      <EmptyState
        title="Could not load billing"
        body={error instanceof ApiError ? error.message : "Please try again."}
      />
    </Card>
  );
}
